// Listanäkymän tila (suodattimet, haku, sivutus, järjestys) pidetään URL:n queryssä, jotta refresh ja
// takaisin-nuoli palauttavat saman näkymän ja linkin voi jakaa. Muistettavat kentät (remember) tallennetaan
// lisäksi localStorageen: kun listalle tullaan ilman parametreja (esim. valikosta), viimeksi käytetyt palautetaan.
//
// Kenttäskeema: { nimi: { type: 'list' | 'int' | 'bool' | 'string', default, allowed?, nullable?, remember? } }
// Taulukon kentät page/size/sort/desc kytketään v-data-tableen: :options="tableOptions" @update:options="onTableOptions".
// Näkymän pitää toteuttaa reload(), jota kutsutaan aina kun query muuttuu. Scroll-kohta palautuu takaisin-navigoinnissa
// automaattisesti (helpers/scrollMemory): reload()in palauttama Promise kertoo, milloin data on ladattu.

import { registerScrollMemory } from '@/helpers/scrollMemory';

const TABLE_FIELDS = ['page', 'size', 'sort', 'desc'];

const defaultOf = field => (typeof field.default === 'function' ? field.default() : field.default);
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Palauttaa undefined, jos arvo ei kelpaa – silloin käytetään oletusta (URL:ia voi muokata käsin)
function parseValue(raw, field) {
  switch (field.type) {
    case 'list': {
      if (raw === '') return [];
      const values = String(raw)
        .split(',')
        .filter(v => !field.allowed || field.allowed.includes(v));
      return values.length > 0 ? values : undefined;
    }
    case 'int': {
      if (field.nullable && raw === 'all') return null;
      const n = parseInt(raw, 10);
      return Number.isNaN(n) || (field.allowed && !field.allowed.includes(n)) ? undefined : n;
    }
    case 'bool':
      return raw === '1' ? true : raw === '0' ? false : undefined;
    default:
      return String(raw);
  }
}

function formatValue(value, field) {
  if (field.type === 'list') return value.join(',');
  if (field.type === 'bool') return value ? '1' : '0';
  if (value === null) return 'all';
  return String(value);
}

export function parseQuery(query, fields) {
  const state = {};
  Object.keys(fields).forEach(name => {
    const parsed = query[name] != null ? parseValue(query[name], fields[name]) : undefined;
    state[name] = parsed !== undefined ? parsed : defaultOf(fields[name]);
  });
  return state;
}

// Vain oletuksesta poikkeavat arvot, jotta oletusnäkymän URL pysyy siistinä
export function toQuery(state, fields) {
  const query = {};
  Object.keys(fields).forEach(name => {
    if (!same(state[name], defaultOf(fields[name]))) query[name] = formatValue(state[name], fields[name]);
  });
  return query;
}

function readStored(storageKey) {
  try {
    return JSON.parse(localStorage.getItem(storageKey)) || {};
  } catch (e) {
    return {};
  }
}

// v-model-kytkennät queryn kenttiin, esim. computed: { ...queryModels({ onlyMine: 'mine' }) }
export function queryModels(mapping) {
  const computed = {};
  Object.keys(mapping).forEach(prop => {
    const name = mapping[prop];
    computed[prop] = {
      get() {
        return this.listState[name];
      },
      set(value) {
        this.updateQuery({ [name]: value });
      },
    };
  });
  return computed;
}

export default function listQuery({ key, fields }) {
  const storageKey = route => `listQuery_${key}_${route.params.tenantId}`;
  const rememberOnly = query =>
    Object.keys(query)
      .filter(name => fields[name].remember)
      .reduce((out, name) => ({ ...out, [name]: query[name] }), {});

  return {
    // Rekisteröidään ennen immediate-watcheria, joka käynnistää ensimmäisen latauksen
    beforeCreate() {
      this.listScrollMemory = registerScrollMemory(this.$route.fullPath);
    },
    beforeDestroy() {
      this.listScrollMemory.unregister();
    },
    computed: {
      listState() {
        return parseQuery(this.$route.query, fields);
      },
      hasListFilters() {
        return Object.keys(toQuery(this.listState, fields)).some(name => !TABLE_FIELDS.includes(name));
      },
      tableOptions() {
        const s = this.listState;
        return { page: s.page, itemsPerPage: s.size, sortBy: s.sort ? [s.sort] : [], sortDesc: s.sort ? [s.desc] : [] };
      },
    },
    watch: {
      '$route.query': {
        immediate: true,
        handler(query) {
          // Tultiin ilman parametreja (valikko): palautetaan viimeksi käytetyt. Tehdään replace-kutsulla vasta
          // navigoinnin jälkeen, jotta korvataan listan oma historiamerkintä eikä edellistä sivua.
          if (Object.keys(query).length === 0) {
            const stored = readStored(storageKey(this.$route));
            if (Object.keys(stored).length > 0) {
              this.$router.replace({ query: stored }).catch(() => {});
              return;
            }
          }
          Promise.resolve(this.reload())
            .then(() => this.$nextTick())
            .then(this.listScrollMemory.markDataLoaded);
        },
      },
    },
    methods: {
      // Suodatinmuutos palauttaa sivulle 1. Sama arvo ei navigoi (estää tuplalataukset ja silmukat).
      updateQuery(patch) {
        const changed = Object.keys(patch).filter(name => !same(patch[name], this.listState[name]));
        if (changed.length === 0) return;
        const state = { ...this.listState, ...patch };
        if (!changed.includes('page')) state.page = 1;
        const query = toQuery(state, fields);
        try {
          localStorage.setItem(storageKey(this.$route), JSON.stringify(rememberOnly(query)));
        } catch (e) {
          // localStorage ei käytettävissä – URL riittää
        }
        this.$router.replace({ query }).catch(() => {});
      },
      // v-data-table emittoi myös mountissa; muuttumattomat arvot ohitetaan updateQueryssä
      onTableOptions(options) {
        const s = this.listState;
        const sort = options.sortBy[0] || '';
        const patch = { size: options.itemsPerPage, sort, desc: sort ? !!options.sortDesc[0] : s.desc };
        const sortOrSizeChanged = Object.keys(patch).some(name => !same(patch[name], s[name]));
        this.updateQuery(sortOrSizeChanged ? patch : { page: options.page });
      },
      resetListQuery() {
        try {
          localStorage.removeItem(storageKey(this.$route));
        } catch (e) {
          // ei tallennettua tilaa
        }
        this.$router.replace({ query: {} }).catch(() => {});
      },
    },
  };
}
